/**
 * EnemyParticle.js
 */

import { Game, GameAnimatedSprite, GameDisplayContainer, GameTween } from "pixi_core";

export class EnemyParticle extends GameDisplayContainer {
    /**
     * @public
     * @param {Object} particleGraphicData
     * @param {Object} trailGraphicData
     * @param {Function} callback
     */
    constructor(particleGraphicData, trailGraphicData, callback) {
        super();

        /** @private @type {Function} */
        this._callback = callback;

        /** @private @type {GameAnimatedSprite} */
        this._particle = new GameAnimatedSprite(null, particleGraphicData);

        /** @private @type {GameAnimatedSprite} */
        this._trail = new GameAnimatedSprite(null, trailGraphicData);

        /** @private @type {Number} */
        this._lastX = 0;
        /** @private @type {Number} */
        this._lastY = 0;

        /** @private @type {GameTween} */
        this._tweenMove = null;

        this.addChild(this._trail);
        this.addChild(this._particle);
    }

    /**
     * @public
     * @param {Number} startX
     * @param {Number} startY
     * @param {Number} controlPt1X
     * @param {Number} controlPt1Y
     * @param {Number} controlPt2X
     * @param {Number} controlPt2Y
     * @param {Number} targetX
     * @param {Number} targetY
     * @param {Number} duration
     */
    _setMovementCurve(startX, startY, controlPt1X, controlPt1Y, controlPt2X, controlPt2Y, targetX, targetY, duration) {
        this.x = startX;
        this.y = startY;

        this._lastX = startX;
        this._lastY = startY;

        this._particle.playAnimation("travel", true);
        this._trail.playAnimation("travel", true);

        this._tweenMove = Game.windows
            .getCurrentWindow()
            .createTween({ t: 0 })
            .to({ t: 1 }, duration)
            .onUpdate((transition) => {
                this.x = this._bezierX(transition.t, startX, controlPt1X, controlPt2X, targetX);
                this.y = this._bezierY(transition.t, startY, controlPt1Y, controlPt2Y, targetY);

                const deltaX = this.x - this._lastX;
                const deltaY = this.y - this._lastY;
                this.rotation = Math.atan2(deltaY, deltaX) + Math.PI;

                this._lastX = this.x;
                this._lastY = this.y;
            })
            .easing(GameTween.Easing.Linear)
            .onComplete(() => {
                if (this._callback) {
                    this._callback(this);
                }

                if (this._particle) {
                    this._particle.playAnimation("impact", false, { hideOnCompleted: true });
                }

                if (this._trail) {
                    this._trail.destroy();
                    this._trail = null;
                }
            })
            .start();
    }

    /**
     * @protected
     * @param {Number} t
     * @param {Number} p0x
     * @param {Number} p1x
     * @param {Number} p2x
     * @param {Number} p3x
     * @returns {Number}
     */
    _bezierX(t, p0x, p1x, p2x, p3x) {
        const cX = 3 * (p1x - p0x);
        const bX = 3 * (p2x - p1x) - cX;
        const aX = p3x - p0x - cX - bX;

        return aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0x;
    }

    /**
     * @protected
     * @param {Number} t
     * @param {Number} p0y
     * @param {Number} p1y
     * @param {Number} p2y
     * @param {Number} p3y
     */
    _bezierY(t, p0y, p1y, p2y, p3y) {
        let cY = 3 * (p1y - p0y);
        let bY = 3 * (p2y - p1y) - cY;
        let aY = p3y - p0y - cY - bY;

        return aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0y;
    }

    /**
     * @public
     */
    destroy() {
        if (this._tweenMove) {
            this._tweenMove.destroy();
            this._tweenMove = null;
        }

        if (this._particle && !this._particle.destroyed) {
            this._particle.destroy();
        }
        this._particle = null;

        if (this._trail && !this._trail.destroyed) {
            this._trail.destroy();
        }
        this._trail = null;

        if (!this.destroyed) {
            super.destroy();
        }
    }
}
